import { ArrowLeft, Pause, Search } from "lucide-react";
import { Fragment, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, cx, ErrorNote, Spinner, toast } from "../../components/ui";
import { api, useApi, type Me } from "../../lib/api";

type ValueSource = { kind: "literal"; value: string } | { kind: "prompt"; code: string; format?: string } | { kind: "builtin"; name: string; arg?: string } | { kind: "report"; template: string };
interface Step {
  id: string;
  seq: number;
  name: string;
  kind: string;
  infor?: { class: string; action: string; submit: string; fields: { name: string; value: ValueSource }[] };
  when?: { prompt: string; equals: string };
  pre?: string[];
  verify: { method: string; flag?: string; function?: string; cls?: string; action?: string; set?: string };
  radar?: { op: string };
  pollSeconds: number;
  maxChecks: number;
  breakAfter: boolean;
  reports: { name: string; template: string }[];
  archives: { report: string; folder: string; file: string }[];
  ownReport?: string;
  instructions?: string;
}
interface Definition {
  id: string;
  type: string;
  name: string;
  version: number;
  runKey: string[];
  runDatePrompt: string;
  builtins: string[];
  prompts: { code: string; label: string; legacyName?: string; kind: string; required: boolean; default?: string; options?: { value: string; label: string }[]; lookup?: { builtin: string } }[];
  trunk: Step[];
  templates: { id: string; source: string }[];
  source?: { files: Record<string, { name: string; bytes: number }>; importedAt: string; importedBy: string };
}
interface Check {
  action: string;
  kind: "plain" | "background" | "fanout" | "unknown" | "pending";
  note?: string;
  finishing?: { cls?: string; action: string };
  source?: string;
  check: { method: string; cls?: string; action?: string };
  why?: string;
}
const KIND: Record<Check["kind"], string> = {
  plain: "Runs inside the call: done when Infor accepts it",
  background: "Runs in background: follows the request Infor creates",
  fanout: "Starts a background group: waits for its finishing action",
  unknown: "Not in delivered source: the legacy check is used",
  pending: "Waiting for Waypoint to read this action",
};

interface Note {
  level: "mapped" | "dropped" | "review" | "error";
  where: string;
  message: string;
}

const VERIFY: Record<string, (s: Step) => string> = {
  asyncActionGroup: () => "Action group completes",
  paymentScheduleFlag: (s) => `Run flag ${s.verify.flag}`,
  pfiWorkunit: () => "Work unit finishes",
  manual: () => "Manual (done on submit)",
  custom: (s) => `Custom: ${s.verify.function}`,
  newRequest: (s) => `New ${s.verify.cls ? `${s.verify.cls}.` : ""}${s.verify.action} request completes`,
  replicationSet: (s) => `Replication set ${s.verify.set} finishes`,
  compassQuery: () => "Compass query finishes",
  radarJob: () => "RADAR job completes",
};
const fmt = (v: ValueSource) =>
  v.kind === "literal" ? (v.value === "" ? "(blank)" : `"${v.value}"`) : v.kind === "prompt" ? `{${v.code}${v.format ? ` as ${v.format.toUpperCase()}` : ""}}` : v.kind === "builtin" ? `built-in ${v.name}${v.arg ? `("${v.arg}")` : ""}` : `report template ${v.template}`;

/** Read-only view of an imported automation (phase 2): prompts, steps and the import report. */
export default function JaAutomation({ me }: { me?: Me }) {
  const { id = "" } = useParams();
  const { data, error, loading, reload } = useApi<{ definition: Definition; notes: Note[]; checks: Record<string, Check>; reruns?: Record<string, { field: string; default: string; flag?: string; required: boolean; source: string }>; head: { versions: { version: number; at: string; by: string; note: string }[] } }>(`/apps/ja/automations/${id}`);
  const designer = !!me?.apps?.find((a) => a.id === "ja")?.roles.includes("designer");
  const [asking, setAsking] = useState(false);
  const askWaypoint = async () => {
    setAsking(true);
    try {
      const r = await api<{ asked: number }>(`/apps/ja/automations/${id}/classify`, { body: {} });
      toast(`Asked Waypoint about ${r.asked} actions. Results appear here in a minute.`);
      setTimeout(() => void reload(), 20000);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setAsking(false);
    }
  };
  const [open, setOpen] = useState<string | null>(null);
  const [levels, setLevels] = useState<Note["level"][]>(["error", "review", "dropped"]);
  const notes = useMemo(() => (data?.notes ?? []).filter((n) => levels.includes(n.level)), [data, levels]);
  if (loading && !data) return <Spinner />;
  if (error || !data) return <ErrorNote>{error ?? "Not found"}</ErrorNote>;
  const d = data.definition;
  const counts = (l: Note["level"]) => data.notes.filter((n) => n.level === l).length;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/apps/ja" className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mb-3">
          <ArrowLeft className="size-4" aria-hidden /> Automations
        </Link>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-2xl font-bold text-ink">
            {d.name} <span className="text-muted font-normal text-base">· {d.type} · v{d.version}</span>
          </h1>
          {designer && (
            <Link to={`/apps/ja/automations/${id}/design`} className="inline-flex items-center gap-1.5 rounded-lg bg-accent text-accent-ink px-3 h-9 text-sm font-medium hover:bg-accent-hover">
              Open in Designer
            </Link>
          )}
        </div>
        <p className="text-sm text-muted">
          Run key: {d.runKey.join(" + ")} + type. {d.trunk.length} steps, {d.trunk.filter((s) => s.breakAfter).length} breaks, {d.trunk.reduce((n, s) => n + s.reports.length + (s.ownReport ? 1 : 0), 0)} reports.
          {d.source && ` Imported ${new Date(d.source.importedAt).toLocaleString()} by ${d.source.importedBy}.`}
        </p>
      </div>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="font-display font-semibold text-ink">Steps</h2>
          {designer && (Object.keys(data.checks ?? {}).length > 0 || data.definition.trunk.some((s) => s.infor && s.infor.class !== "AsyncActionRequest")) && (
            <Button onClick={() => void askWaypoint()} busy={asking}>
              <Search className="size-4" aria-hidden /> Check actions with Waypoint
            </Button>
          )}
        </div>
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Step</th>
                <th className="px-3 py-2 font-medium">Infor</th>
                <th className="px-3 py-2 font-medium">Done when</th>
                <th className="px-3 py-2 font-medium">Reports</th>
              </tr>
            </thead>
            <tbody>
              {d.trunk.map((s, i) => (
                <Fragment key={s.id}>
                  <tr className="border-b border-line align-top cursor-pointer hover:bg-surface-2" onClick={() => setOpen(open === s.id ? null : s.id)}>
                    <td className="px-3 py-2 text-muted tabular">{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-ink flex items-center gap-1.5">
                        {s.name}
                        {s.breakAfter && <Pause className="size-3.5 text-warn" aria-label="Pauses for review" />}
                      </div>
                      <div className="text-xs text-muted font-mono">{s.id}</div>
                      {s.when && <div className="text-xs text-warn">Only when {s.when.prompt} = {s.when.equals}</div>}
                      {data.reruns?.[s.id] && (
                        <div className="text-xs text-accent-text">
                          Can rerun ({data.reruns[s.id].field}, {data.reruns[s.id].default === "auto" ? `Auto by ${data.reruns[s.id].flag}` : data.reruns[s.id].default === "yes" ? "Yes" : "No"} by default{data.reruns[s.id].required ? "; Infor requires it for a second run" : ""}{data.reruns[s.id].source === "waypoint" ? "; found by Waypoint" : ""})
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {s.kind === "compass" ? (
                        <div className="text-xs">Compass SQL</div>
                      ) : s.kind === "radar" ? (
                        <div className="text-xs">RADAR {s.radar?.op}</div>
                      ) : s.infor ? (
                        <>
                          <div className="font-mono text-xs">
                            {s.infor.class}.{s.infor.action}
                          </div>
                          <div className="text-xs text-muted">{s.kind === "report" ? "report request" : s.infor.submit === "async" ? "background request" : "action"}</div>
                        </>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-ink-2">
                      {data.checks?.[s.id] ? (
                        <>
                          {data.checks[s.id].check.method === "newRequest" ? `New ${data.checks[s.id].check.cls ? `${data.checks[s.id].check.cls}.` : ""}${data.checks[s.id].check.action} request completes` : (VERIFY[data.checks[s.id].check.method]?.(s) ?? data.checks[s.id].check.method)}
                          <div className={cx("text-xs", data.checks[s.id].kind === "unknown" || data.checks[s.id].kind === "pending" ? "text-muted" : "text-accent-text")} title={data.checks[s.id].source ?? data.checks[s.id].note}>
                            Waypoint: {KIND[data.checks[s.id].kind]}
                          </div>
                        </>
                      ) : (
                        (VERIFY[s.verify.method]?.(s) ?? s.verify.method)
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-2">
                      {[...(s.ownReport ? [s.ownReport] : []), ...s.reports.map((r) => r.name)].join(", ") || <span className="text-muted">—</span>}
                      {s.archives.length > 0 && <div className="text-muted">archived</div>}
                    </td>
                  </tr>
                  {open === s.id && (
                    <tr className="border-b border-line bg-surface-2">
                      <td />
                      <td colSpan={4} className="px-3 py-3 text-xs space-y-2">
                        {s.instructions && <p className="text-ink-2">{s.instructions}</p>}
                        {data.checks?.[s.id] && (
                          <p className="text-ink-2">
                            {data.checks[s.id].action}: {data.checks[s.id].why ?? data.checks[s.id].note ?? KIND[data.checks[s.id].kind]}
                            {data.checks[s.id].source && <span className="text-muted"> ({data.checks[s.id].source})</span>}
                          </p>
                        )}
                        {s.pre && <p>Before submit: {s.pre.join(", ")}</p>}
                        {s.infor && s.infor.fields.length > 0 && (
                          <table className="w-full">
                            <tbody>
                              {s.infor.fields.map((f) => (
                                <tr key={f.name}>
                                  <td className="pr-4 py-0.5 font-mono">{f.name}</td>
                                  <td className="py-0.5 text-ink-2">{fmt(f.value)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                        {s.archives.map((a) => (
                          <p key={a.report}>
                            Archive "{a.report}" to DOWNLOADS/{a.folder}
                            {a.file}
                          </p>
                        ))}
                        <p className="text-muted">
                          Checks every {s.pollSeconds}s, up to {s.maxChecks} times.
                        </p>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted mt-1">Select a step to see the fields it sends.</p>
      </section>

      <section>
        <h2 className="font-display font-semibold text-ink mb-2">Prompts</h2>
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <tbody>
              {d.prompts.map((p) => (
                <tr key={p.code} className="border-b border-line last:border-0">
                  <td className="px-3 py-2">
                    <div className="text-ink">{p.label}</div>
                    <div className="text-xs text-muted font-mono">
                      {p.code}
                      {p.legacyName && ` · ${p.legacyName}`}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-ink-2">
                    {p.kind}
                    {p.lookup && ` · lookup ${p.lookup.builtin}`}
                    {p.options && ` · ${p.options.map((o) => o.value).join("/")}`}
                  </td>
                  <td className="px-3 py-2 text-ink-2">{p.default ? `default ${p.default}` : ""}</td>
                  <td className="px-3 py-2 text-xs">{p.required ? "required" : ""}{d.runKey.includes(p.code) ? " · run key" : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h2 className="font-display font-semibold text-ink">Import report</h2>
          <div className="flex gap-3 text-xs">
            {(["error", "review", "dropped", "mapped"] as const).map((l) => (
              <label key={l} className="inline-flex items-center gap-1">
                <input type="checkbox" checked={levels.includes(l)} onChange={(e) => setLevels((cur) => (e.target.checked ? [...cur, l] : cur.filter((x) => x !== l)))} />
                {l === "error" ? "To fix" : l === "review" ? "To review" : l === "dropped" ? "Dropped" : "Mapped"} ({counts(l)})
              </label>
            ))}
          </div>
        </div>
        <ul className="rounded-xl border border-line bg-surface divide-y divide-line text-sm">
          {notes.length === 0 && <li className="px-4 py-3 text-muted">Nothing in the selected groups.</li>}
          {notes.map((n, i) => (
            <li key={i} className="px-4 py-2 flex gap-3">
              <span className={cx("text-xs font-semibold uppercase w-16 shrink-0 pt-0.5", n.level === "error" ? "text-bad" : n.level === "review" ? "text-warn" : n.level === "dropped" ? "text-muted" : "text-good")}>{n.level === "error" ? "fix" : n.level}</span>
              <span className="text-xs text-muted w-56 shrink-0 font-mono break-all">{n.where}</span>
              <span className="text-ink-2">{n.message}</span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted mt-1">Built-ins standing in for legacy functions until the function sandbox (phase 4): {d.builtins.join(", ")}.</p>
      </section>
    </div>
  );
}
